import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {createStackNavigator, StackNavigationProp, StackScreenProps} from '@react-navigation/stack'

import {
  GetCurrentRouteName,
  GetSignupWorkflowNavigationOptions,
  OffersMinPreReqsNotMetSelector,
  useSetupRouteHistory,
} from 'src/workflows/navigation'
import {
  SignupWorkflowStackParams,
  WorkflowsErrorType,
  WorkflowsLoadingType,
  WorkflowsReadyType,
  WorkflowsStackParams,
} from 'src/workflows/types'

import {AddressWorkflowContainer} from 'src/products/MCU/Address/AddressWorkflowContainer'
import {AppEvents} from 'src/lib/Analytics/app_events'
import {ApplicationStepsContainer} from 'src/products/general/ApplicationSteps/ApplicationStepsContainer'
import {ApplyInOneClickContainer} from 'src/products/general/ApplyInOneClick/ApplyInOneClickContainer'
import {ErrorTemplate} from 'src/designSystem/components/templates/ErrorTemplate/ErrorTemplate'
import {GetNextRouteFromPreReqs, GetSortedPreReqs} from 'src/workflows/workflow.utils'
import {LoanAmountSelectionWorkflowContainer} from 'src/products/loans/LoanAmountSelection/LoanAmountSelectionWorkflowContainer'
import {MainStackParamList} from 'src/nav/MainStackParamsList'
import {NotificationsPermissionsContainer} from 'src/products/general/NotificationsPermissions/NotificationsPermissionsContainer'
import {PhoneNumberInputVerificationContainer} from 'src/products/MCU/PhoneNumberInputVerification/PhoneNumberInputVerificationContainer'
import {PlaidLayersProvider} from 'src/products/MCU/PlaidLayers/PlaidLayersContext'
import {SignupWorkflowSortSelector} from 'src/products/general/SignupWorkflow/SignupWorkflowSorting'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {useCanAccessCards} from 'src/products/card/LoanDash/useCanAccessCards'
import {useMoveToNextPreReqRoute} from 'src/workflows/hooks/useMoveToNextPreReqRoute'
import {useWorkflows} from 'src/workflows/hooks/useWorkflows'
import {useWorkflowsSort} from 'src/workflows/experimentation'
import {VerifyYourIdentityWorkflowContainer} from 'src/products/MCU/VerifyYourIdentity/VerifyYourIdentityWorkflowContainer'
import {wfLog} from 'src/workflows/logging'
import Box from 'src/designSystem/components/atoms/Box/Box'
import {Loading} from 'src/designSystem/components/atoms/Loading/Loading'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import {StateSelectWorkflowContainer} from 'src/products/general/StateSelect/StateSelectWorkflowContainer'
import {usePushPermissions} from 'src/lib/usePushPermissions'
import {getIsAppRevampFunctionalUpdatesEnabled} from 'src/lib/experimentation/appRevampFeatureFlag'
import {WorkflowDebugPanel} from 'src/products/general/Workflows/WorkflowDebugPanel'
import {WorkflowLoading} from 'src/products/general/Workflows/WorkflowLoading'
import {useIsAdvance} from 'src/lib/advance/useIsAdvance/useIsAdvance'
import {MilitarySurveyWorkflowContainer} from 'src/products/general/MilitarySurvey/MilitarySurveyWorkflowContainer'

export const SignUpStackNav = createStackNavigator<SignupWorkflowStackParams>()

const SignupWorkflowReady: React.FC<
  StackScreenProps<MainStackParamList, 'SignupWorkflow'> &
    WorkflowsReadyType & {
      isPushPermissionsEnabled: boolean
      isAdvance: boolean
    }
> = (props) => {
  const {unmetMinPreReqs, metMinPreReqs, refetch, navigation, isPushPermissionsEnabled, isAdvance} =
    props

  const isCardUser = useCanAccessCards().canAccessCards

  const isAppRevampFunctionalUpdatesFFEnabled = getIsAppRevampFunctionalUpdatesEnabled()

  const showNotificationsPermissionsFirst =
    isAppRevampFunctionalUpdatesFFEnabled && !isPushPermissionsEnabled

  const showApplicationStepsFirst =
    isAppRevampFunctionalUpdatesFFEnabled && isPushPermissionsEnabled

  const [signUpNavigation, setSignUpNavigation] =
    useState<StackNavigationProp<WorkflowsStackParams>>()

  const initialSignupWorkflowScreen = showApplicationStepsFirst
    ? 'ApplicationSteps'
    : showNotificationsPermissionsFirst
      ? 'NotificationsPermissions'
      : undefined

  wfLog('Met min pre-reqs: ', metMinPreReqs)

  useSetupRouteHistory({
    navigation: signUpNavigation,
    unmetPreReqs: unmetMinPreReqs,
    metPreReqs: metMinPreReqs,
    initialRouteName: initialSignupWorkflowScreen,
  })

  useWorkflowsSort({
    selectedOffer: null,
    unmetPreReqs: unmetMinPreReqs,
    preReqSortSelector: SignupWorkflowSortSelector,
    passthrough: {
      isLoanOnboarding: !isCardUser,
    },
  })

  const moveToNextPreReqRoute = useMoveToNextPreReqRoute({
    met: metMinPreReqs,
    unmet: unmetMinPreReqs,
    workflowName: 'Signup',
  })

  const currentRouteName = useMemo(
    () => GetNextRouteFromPreReqs(unmetMinPreReqs),
    [unmetMinPreReqs],
  )
  wfLog('Current route name: ', currentRouteName)

  useEffect(() => {
    TrackAppEvent(AppEvents.Name.signup_workflow_entered, AppEvents.Category.Workflows, {
      route: currentRouteName,
    })
    return (): void => {
      TrackAppEvent(AppEvents.Name.signup_workflow_exited, AppEvents.Category.Workflows)
    }
    // we don't want to track this on every render; just on mount and unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOnPreReqFulfilled = useCallback(async () => {
    await moveToNextPreReqRoute({
      navigation,
      workflowNavigation: signUpNavigation,
      refetch,
      preReqSelector: OffersMinPreReqsNotMetSelector,
    })
  }, [moveToNextPreReqRoute, navigation, signUpNavigation, refetch])

  wfLog(`\nNavigation State: ${JSON.stringify(navigation.getState())}`)

  const handleNavigationLoaded = useCallback(
    (signUpNav: StackNavigationProp<WorkflowsStackParams>) => {
      setSignUpNavigation(signUpNav)
    },
    [],
  )

  return (
    <PlaidLayersProvider onPreReqFulfilled={handleOnPreReqFulfilled} navigation={signUpNavigation}>
      <SignUpStackNav.Navigator
        initialRouteName={'Loading'}
        screenOptions={{
          gestureEnabled: true,
          headerTransparent: true,
          headerShadowVisible: false,
          headerMode: 'float',
        }}
      >
        <SignUpStackNav.Screen name="Loading" options={GetSignupWorkflowNavigationOptions}>
          {/** Loading route initializes navigation within SignUpStackNav context, ensuring navigation points to the correct stack */}
          {(): React.ReactNode => <WorkflowLoading onNavigationLoaded={handleNavigationLoaded} />}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen name="StateSelect" options={GetSignupWorkflowNavigationOptions}>
          {(screenProps): React.ReactNode => {
            return (
              <StateSelectWorkflowContainer
                {...screenProps}
                onPreReqFulfilled={handleOnPreReqFulfilled}
              />
            )
          }}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen
          name="NotificationsPermissions"
          options={GetSignupWorkflowNavigationOptions}
        >
          {(screenProps): React.ReactNode => (
            <NotificationsPermissionsContainer
              {...screenProps}
              onPreReqFulfilled={handleOnPreReqFulfilled}
            />
          )}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen
          name="LoanAmountSelection"
          options={GetSignupWorkflowNavigationOptions}
        >
          {(screenProps): React.ReactNode => {
            return (
              <LoanAmountSelectionWorkflowContainer
                {...screenProps}
                onPreReqFulfilled={handleOnPreReqFulfilled}
              />
            )
          }}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen
          name="PhoneConfirmation"
          options={GetSignupWorkflowNavigationOptions}
        >
          {(screenProps): React.ReactNode => (
            <PhoneNumberInputVerificationContainer
              {...screenProps}
              onPreReqFulfilled={handleOnPreReqFulfilled}
            />
          )}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen
          name="PersonalInformation"
          options={GetSignupWorkflowNavigationOptions}
        >
          {(screenProps): React.ReactNode => (
            <VerifyYourIdentityWorkflowContainer
              {...screenProps}
              onPreReqFulfilled={handleOnPreReqFulfilled}
            />
          )}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen name="AddressHome" options={GetSignupWorkflowNavigationOptions}>
          {(screenProps): React.ReactNode => (
            <AddressWorkflowContainer
              {...screenProps}
              onPreReqFulfilled={handleOnPreReqFulfilled}
            />
          )}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen name="ApplyInOneClick" options={GetSignupWorkflowNavigationOptions}>
          {(screenProps): React.ReactNode => (
            <ApplyInOneClickContainer
              {...screenProps}
              onPreReqFulfilled={handleOnPreReqFulfilled}
            />
          )}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen name="ApplicationSteps" options={GetSignupWorkflowNavigationOptions}>
          {(screenProps): React.ReactNode => (
            <ApplicationStepsContainer
              {...screenProps}
              onPreReqFulfilled={handleOnPreReqFulfilled}
              isAdvance={isAdvance}
            />
          )}
        </SignUpStackNav.Screen>
        <SignUpStackNav.Screen name="MilitarySurvey" options={GetSignupWorkflowNavigationOptions}>
          {(screenProps): React.ReactNode => (
            <MilitarySurveyWorkflowContainer
              {...screenProps}
              onPreReqFulfilled={handleOnPreReqFulfilled}
            />
          )}
        </SignUpStackNav.Screen>
      </SignUpStackNav.Navigator>
      <ReadyDebug {...props} />
    </PlaidLayersProvider>
  )
}

const SignupWorkflowLoading: React.FC<WorkflowsLoadingType> = () => {
  return (
    <Box fill align="center" justify="center">
      <Loading size="large" type="loader0" />
    </Box>
  )
}

const SignupWorkflowError: React.FC<WorkflowsErrorType> = (props) => {
  const {error, refetch: handleRefetch} = props

  useEffect(() => {
    TrackAppEvent(AppEvents.Name.signup_workflow_error, AppEvents.Category.Workflows, {
      error: error.message,
    })
  }, [error.message])

  return <ErrorTemplate onRetry={handleRefetch} />
}

const SignupWorkflow: React.FC<StackScreenProps<MainStackParamList, 'SignupWorkflow'>> = (
  props,
) => {
  const workflows = useWorkflows()
  const [isPushPermissionsEnabled, , isPushPermissionsFetching] = usePushPermissions()
  const {isAdvance, loading: isAdvanceLoading} = useIsAdvance()

  if (isPushPermissionsFetching || isAdvanceLoading) {
    return <SignupWorkflowLoading loading={true} status="LOADING" />
  }

  switch (workflows.status) {
    case 'READY':
      return (
        <SignupWorkflowReady
          {...workflows}
          {...props}
          isPushPermissionsEnabled={isPushPermissionsEnabled}
          isAdvance={isAdvance}
        />
      )
    case 'LOADING':
      return <SignupWorkflowLoading {...workflows} />
    case 'ERROR':
      return <SignupWorkflowError {...workflows} />
    default:
      return (
        <Box fill>
          <Loading type="loader0" size="large" />
        </Box>
      )
  }
}

export {SignupWorkflow}

const ReadyDebug: React.FC<
  StackScreenProps<MainStackParamList, 'SignupWorkflow'> & WorkflowsReadyType
> = (props) => {
  const {unmetMinPreReqs, navigation} = props

  const nextRoute = useMemo(() => GetNextRouteFromPreReqs(unmetMinPreReqs), [unmetMinPreReqs])
  const sortedPreReqs = useMemo(() => GetSortedPreReqs(unmetMinPreReqs), [unmetMinPreReqs])

  const navState = navigation.getState()

  const details = useMemo(() => {
    if (!__DEV__) {
      return null
    }

    return (
      <>
        {sortedPreReqs.length > 0 ? (
          <PFText variant="p_sm" color="inverse">
            Current PreReq: {sortedPreReqs[0]}
          </PFText>
        ) : null}
        <PFText variant="p_sm" color="inverse">
          Current Route: {nextRoute}
        </PFText>
        <PFText variant="p_sm" color="inverse">
          Visible Route: {GetCurrentRouteName(navState)}
        </PFText>
      </>
    )
  }, [navState, nextRoute, sortedPreReqs])

  return <WorkflowDebugPanel title="Signup Workflow Debug">{details}</WorkflowDebugPanel>
}
